'use client'

import { useState } from 'react'

import { ChevronDown, ChevronRight } from 'lucide-react'

import { SplitsLink as Link } from '~/components/Link'
import { Tooltip } from '~/components/component-library'
import { activateOnEnterOrSpace } from '~/components/component-library/List/helpers'
import { ROW_FOCUS_RING } from '~/components/component-library/List/styles'
import { ListItemProps } from '~/components/component-library/List/types'
import {
  OneLineFragment,
  TwoLineFragment,
} from '~/components/component-library/internal/Fragments'
import { baseConfig } from '~/components/component-library/internal/config'
import { cn, INTERACTIVE_ROW_HOVER_BG } from '~/utils/tailwind-utils'

export const ListItem = (props: ListItemProps) => {
  const inner = <ListItemInner {...props} />
  return props.tooltip ? (
    <Tooltip className="w-full" content={props.tooltip}>
      {inner}
    </Tooltip>
  ) : (
    inner
  )
}

const ListItemInner = ({
  content,
  image,
  href,
  onClick,
  actionIcon,
  description,
  meta,
  isDisabled,
  expandableContent,
  initialExpanded,
}: ListItemProps) => {
  const [expanded, setExpanded] = useState(initialExpanded ?? false)

  const isLinkRow = !!href && !isDisabled
  const isHandlerRow = (!!onClick || !!expandableContent) && !isDisabled
  const isClickable = isLinkRow || isHandlerRow

  let suffix: React.ReactNode = actionIcon
  if (!suffix && isLinkRow) {
    suffix = <ChevronRight />
  } else if (!suffix && expandableContent) {
    suffix = <ChevronDown className={cn({ 'rotate-180': expanded })} />
  }

  const handleLocalClick = (e: React.MouseEvent) => {
    if (onClick) onClick(e)
    else if (expandableContent) setExpanded(!expanded)
    // Mouse activation drops keyboard focus so the ring goes away after a
    // click. Keyboard activation (Enter/Space) has detail === 0 so focus is
    // preserved for continued navigation. Scope to focus inside this row so we
    // don't yank focus from elsewhere on the page.
    if (e.detail > 0) {
      const active = document.activeElement as HTMLElement | null
      if (active && e.currentTarget.contains(active)) active.blur()
    }
  }

  const rowClassName = cn('flex w-full text-base text-primary', {
    [INTERACTIVE_ROW_HOVER_BG]: isClickable && !expanded,
    'cursor-pointer': isClickable,
    'cursor-default': !isClickable,
  })

  const rowChildren = (
    <OneLineFragment prefix={image} suffix={suffix} meta={meta}>
      <TwoLineFragment topLine={content} bottomLine={description} />
    </OneLineFragment>
  )

  let row
  if (isLinkRow) {
    row = (
      <Link
        path={href.path}
        target={href.target}
        className={cn(rowClassName, baseConfig.rounding, ROW_FOCUS_RING)}
      >
        {rowChildren}
      </Link>
    )
  } else if (isHandlerRow) {
    row = (
      <div onClick={handleLocalClick} className={rowClassName}>
        {rowChildren}
      </div>
    )
  } else {
    row = <div className={rowClassName}>{rowChildren}</div>
  }

  const panel = expandableContent && expanded && (
    <div
      className={cn(
        'w-full !pt-0',
        baseConfig.horizontalPadding,
        baseConfig.verticalPadding,
      )}
    >
      {expandableContent}
    </div>
  )

  // <li> stays unrounded so divide-y dividers render as straight lines; the
  // inner wrapper carries the rounding for the focus ring.
  const liContent = isHandlerRow ? (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => activateOnEnterOrSpace(e, handleLocalClick)}
      className={cn(
        'flex w-full flex-col',
        baseConfig.rounding,
        ROW_FOCUS_RING,
      )}
    >
      {row}
      {panel}
    </div>
  ) : (
    <>
      {row}
      {panel}
    </>
  )

  return (
    <li
      className={cn('flex w-full flex-col', {
        'pointer-events-none opacity-50': isDisabled,
      })}
    >
      {liContent}
    </li>
  )
}
